/**
 * Pure map-dimension computation. No React, no react-native — so it can
 * be unit-tested in a plain node vitest environment.
 *
 * useMapDimensions (in ./useMapDimensions.ts) is a thin React hook
 * wrapper that calls useWindowDimensions and delegates here.
 *
 * ─── Landscape SVG-fills-wrapper policy ──────────────────────────────
 *
 * In landscape (phone or desktop) the SVG's effective viewBox is padded
 * proportionally so the SVG bounds match the wrapper bounds exactly —
 * no horizontal or vertical letterbox inside the wrapper. Concretely:
 *
 *   if aspectWrapper > aspectMap:    // wrapper is wider than the map
 *     pad viewBox horizontally → padded width = mapH × aspectWrapper
 *   else (wrapper is taller):
 *     pad viewBox vertically   → padded height = mapW / aspectWrapper
 *
 * The svgWidth/svgHeight returned for landscape are the wrapper's full
 * width / available height (NOT the original aspect-fit values). The
 * polygon-to-screen pixel scale stays identical to the pre-padding
 * behaviour:
 *
 *   pxPerUnit = svgWidth / paddedW = widthBudget / paddedW
 *             = widthBudget / (mapH × widthBudget/availableHeight)
 *             = availableHeight / mapH
 *             = oldSvgHeight / mapH         (height-bound case)
 *
 * — content renders at the same screen pixel size as the previous
 * aspect-fit behaviour. The user-visible delta is:
 *
 *   (a) Corner-anchored chips (cog, region toggle, zoom toggle) push
 *       to the wrapper corners (= screen corners modulo the action
 *       sidebar). `sidePad = (containerWidth - svgWidth) / 2` is 0 in
 *       landscape now because both equal widthBudget.
 *   (b) The pan-gesture surface covers the full wrapper, including
 *       the padded ocean.
 *   (c) `clampPan` operates on the padded viewBox, so zoomed-in users
 *       can drag the map content INTO the padded space — previously
 *       the pan stopped at the original viewBox edges.
 *
 * Portrait keeps its aspect-fit height and polygon scale, while padding the
 * viewBox horizontally to fill the available width. This expands the zoom/pan
 * viewport into the side gutters without shrinking the map or taking space
 * from the action panel below it.
 *
 * DO NOT revert this without coordinating: corner-overlay positions,
 * pan range, and the player-roster-strip overlay all depend on the
 * SVG-fills-wrapper invariant in landscape. Regression tests in
 * tests/useMapDimensions.test.ts pin the contract — change them in
 * lock-step if you ever need to change the policy.
 */

export interface MapDimensions {
  /** Rendered <svg> width in CSS pixels.
   *  In LANDSCAPE: equals the wrapper's widthBudget — the SVG fills
   *  the available horizontal space (the viewBox is padded to match
   *  so polygons stay at the same screen scale).
   *  In PORTRAIT: widthBudget, with horizontal viewBox padding when needed. */
  svgWidth: number
  /** Rendered <svg> height in CSS pixels.
   *  In LANDSCAPE: equals the available height — fills vertically.
   *  In PORTRAIT: the original aspect-fit height; horizontal padding does not grow it. */
  svgHeight: number
  /** Width of the wrapper that the SVG sits inside.
   *  In LANDSCAPE: equals svgWidth (= widthBudget) — wrapper is flush
   *  with SVG, no letterbox.
   *  In PORTRAIT: widthBudget, matching the SVG viewport. */
  containerWidth: number
  /** True when screenWidth > screenHeight. */
  isLandscape: boolean
  /** True when screenWidth ≥ DESKTOP_BREAKPOINT (1024). */
  isDesktop: boolean
  /** Phase-UI action sidebar width in landscape. Phone landscape:
   * DEFAULT_RESERVED_LEFT_LANDSCAPE (300). Desktop:
   * DEFAULT_RESERVED_LEFT_DESKTOP (380). */
  actionPanelWidth: number
  /** Effective viewBox passed to the <Svg viewBox=…> attr.
   *  In LANDSCAPE: PADDED proportionally so the SVG fills the wrapper.
   *  Padded width/height are larger than the source map's width/height.
   *  Padded x/y are shifted negative so the original content sits
   *  centred inside the larger viewBox.
   *  In PORTRAIT: padded horizontally when height-bound, with unchanged y/height. */
  viewBox: { x: number; y: number; width: number; height: number }
  /** Width (in viewBox units) that element sizes — troop badges, region-
   *  bonus chips, labels, mapW-scaled stroke widths — must be multiplied
   *  by to get a consistent RENDERED pixel size. This is the map's NATIVE
   *  width, deliberately NOT viewBox.width: in landscape the viewBox is
   *  padded horizontally to fill the wrapper, so viewBox.width grows the
   *  wider the screen gets. The on-screen polygon scale is pinned by
   *  height, so sizing off the padded width balloons the numbers on wide
   *  screens while the map itself stays the same size. Keying off the
   *  native width keeps every element proportional to the rendered
   *  polygons regardless of window width or padding. */
  sizingWidth: number
}

export const DESKTOP_BREAKPOINT = 1024
const DESKTOP_HEIGHT_BUDGET = 0.96
const DEFAULT_RESERVED_BOTTOM_PORTRAIT = 220
export const DEFAULT_RESERVED_LEFT_LANDSCAPE = 300
export const DEFAULT_RESERVED_LEFT_DESKTOP = 380
// No right gutter: the landscape player roster used to render as a vertical
// strip floating in a 48px right-edge column, and the map reserved that width
// so polygons didn't sit under it. The roster has since moved INTO the action-
// panel header (see games/[id]/index.tsx: landscapeActionHeader), so nothing
// occupies the right edge anymore — the map should fill flush to it, matching
// the /log + snapshot views. Safe-area right inset is still added separately
// (see reservedRight below), so notched landscape phones keep their inset.
export const DEFAULT_RESERVED_RIGHT_LANDSCAPE = 0
const DEFAULT_RESERVED_TOP_PORTRAIT = 56
const DEFAULT_RESERVED_TOP_LANDSCAPE = 0

/**
 * Compute layout for the given viewport + map viewBox. The breakpoint
 * policy is:
 *
 *   - Phone PORTRAIT:  action panel below the map; no horizontal
 *                      sidebar reservation; bottom reserve = 220 px.
 *   - Phone LANDSCAPE: 300 px left sidebar; 48 px right gutter so
 *                      the vertical roster strip lives in its own
 *                      column without covering map polygons.
 *   - DESKTOP:         380 px left sidebar; 48 px right gutter
 *                      (same reasoning as phone landscape). The map
 *                      wrapper shrinks to svgWidth so it doesn't
 *                      paint dead ocean past the map.
 *
 * Regression tests in tests/useMapDimensions.test.ts pin these
 * values — change the contract there if you change it here.
 */
export function computeMapDimensions(
  screenWidth: number,
  screenHeight: number,
  vbInput: { x?: number; y?: number; width: number; height: number } | undefined,
  opts: {
    reservedTop?: number
    reservedBottom?: number
    reservedLeft?: number
    reservedRight?: number
    /**
     * Extra viewBox padding factor applied AFTER the wrapper-fill
     * padding in landscape. 0 = no extra (default), 0.08 = +8% on
     * each side. Used by iOS-web callers where Safari's landscape
     * layout leaves less visual room for polygons before the corner
     * overlay chips start covering them. Scales the polygons DOWN
     * within the SVG (more "ocean" around the content) without
     * touching aspect.
     */
    extraPaddingFactor?: number
    /**
     * Phone portrait only: viewBox units cut off the top / bottom of the box
     * (the blank authored margins around the territories — see
     * lib/mapContentBounds), so the board runs flush between the bars.
     */
    trimTop?: number
    trimBottom?: number
    /**
     * Device safe-area insets (iPhone Dynamic Island in landscape).
     * Only insets.right is consulted — added to reservedRight in
     * landscape so the map stops at the notch boundary on
     * landscape-right. insets.left is intentionally NOT factored in
     * here; the action-panel header pads itself at the component
     * level so the cog + roster stay visible without shrinking the
     * map's left edge. Pure math here; no React Native deps.
     */
    safeAreaInsets?: { left?: number; right?: number }
  } = {},
): MapDimensions {
  const vb = vbInput ?? { width: 600, height: 900 }
  const isLandscape = screenWidth > screenHeight
  const isDesktop = screenWidth >= DESKTOP_BREAKPOINT
  const portraitPhone = !isLandscape && !isDesktop
  const trimTop = portraitPhone ? Math.min(Math.max(0, opts.trimTop ?? 0), vb.height / 4) : 0
  const trimBottom = portraitPhone ? Math.min(Math.max(0, opts.trimBottom ?? 0), vb.height / 4) : 0
  const width = vb.width
  const height = vb.height - trimTop - trimBottom
  const x = vb.x ?? 0
  const y = (vb.y ?? 0) + trimTop
  const aspect = width / height

  const defaultReservedBottom = isLandscape ? 0 : DEFAULT_RESERVED_BOTTOM_PORTRAIT
  const defaultReservedTop = isLandscape
    ? DEFAULT_RESERVED_TOP_LANDSCAPE
    : DEFAULT_RESERVED_TOP_PORTRAIT
  const defaultReservedLeft = isLandscape
    ? (isDesktop ? DEFAULT_RESERVED_LEFT_DESKTOP : DEFAULT_RESERVED_LEFT_LANDSCAPE)
    : 0
  // Right gutter in landscape (phone + desktop) so the vertical
  // roster strip lives in its own 48 px column without covering
  // the map polygons. Without this reserve, the SVG-fills-wrapper
  // policy can place polygons all the way out to the wrapper's
  // east edge (especially on width-bound maps that get vertical
  // viewBox padding), and the absolute-positioned roster strip
  // ends up sitting ON TOP of country shapes — see PR #258 for
  // the original "roster covering Mexico on USA map" report.
  // Portrait keeps right=0 because the roster lives ABOVE the
  // map in a top header bar there, not next to it.
  const defaultReservedRight = isLandscape ? DEFAULT_RESERVED_RIGHT_LANDSCAPE : 0

  const safeRight = opts.safeAreaInsets?.right ?? 0
  // Asymmetric safe-area handling per user feedback:
  //   - reservedLeft: NEVER adds insets.left. Action panel sits at
  //     SafeAreaView's left edge (which is already insets.left in from
  //     screen left). The action-panel header pads itself with
  //     insets.left so its cog + roster stay visible when the notch
  //     is on the screen's left (landscape-left rotation).
  //   - reservedRight: ADDS insets.right in landscape. The map stops
  //     AT the notch boundary on landscape-right rather than extending
  //     under it — user explicitly asked for 'padding up to the bottom
  //     side of the notch' there. On landscape-left insets.right is 0
  //     so this is a no-op and the map fills the visible width.
  // Result: visible map width is symmetric across both landscape
  // rotations (screenWidth − insets.left − insets.right − 300).
  const reservedLeft = opts.reservedLeft ?? defaultReservedLeft
  const reservedRight = (opts.reservedRight ?? defaultReservedRight) + (isLandscape ? safeRight : 0)
  const widthBudget = Math.max(120, screenWidth - reservedLeft - reservedRight)
  const availableHeight = isDesktop
    ? screenHeight * DESKTOP_HEIGHT_BUDGET
      - (opts.reservedTop ?? 0)
      - (opts.reservedBottom ?? 0)
    : screenHeight -
      (opts.reservedTop ?? defaultReservedTop) -
      (opts.reservedBottom ?? defaultReservedBottom)

  const widthBoundW = widthBudget
  const widthBoundH = widthBudget / aspect
  const heightBoundH = Math.max(120, availableHeight)
  const heightBoundW = heightBoundH * aspect
  let svgWidth: number
  let svgHeight: number
  if (widthBoundH <= heightBoundH) {
    svgWidth = widthBoundW
    svgHeight = widthBoundH
  } else {
    svgWidth = Math.min(widthBudget, heightBoundW)
    svgHeight = heightBoundH
  }

  let containerWidth = isDesktop ? svgWidth : widthBudget
  const actionPanelWidth = isDesktop
    ? DEFAULT_RESERVED_LEFT_DESKTOP
    : DEFAULT_RESERVED_LEFT_LANDSCAPE

  // Landscape: pad the viewBox proportionally so the SVG fills the
  // wrapper area exactly. Same screen-pixel scale for the polygons
  // (svgWidth grows in lock-step with the viewBox), but the extra
  // "ocean" around the original viewBox is now ADDRESSABLE — corner
  // chips anchor to the wrapper edges (= screen corners), the pan
  // gesture has more surface, and clampPan allows the user to drag
  // into the padded space when zoomed in.
  //
  // Portrait widens only the horizontal viewport below; its original fitted
  // height and polygon scale remain fixed, preserving room for the action panel.
  let viewBox = { x, y, width, height }
  if (isLandscape) {
    const heightBound = Math.max(120, availableHeight)
    const aspectWrapper = widthBudget / heightBound
    if (Math.abs(aspectWrapper - aspect) > 1e-6) {
      if (aspectWrapper > aspect) {
        // Wrapper wider than map → pad viewBox horizontally
        const paddedW = height * aspectWrapper
        const padX = (paddedW - width) / 2
        viewBox = { x: x - padX, y, width: paddedW, height }
      } else {
        // Wrapper taller than map → pad viewBox vertically
        const paddedH = width / aspectWrapper
        const padY = (paddedH - height) / 2
        viewBox = { x, y: y - padY, width, height: paddedH }
      }
    }
    // SVG fills the wrapper exactly now — no letterboxing.
    svgWidth = widthBudget
    svgHeight = heightBound
    containerWidth = widthBudget

    // Optional extra padding (iOS web in particular — see the
    // extraPaddingFactor docstring). Scales the polygons DOWN
    // within the SVG so corner chips don't end up overlapping
    // territories. svgWidth/svgHeight stay the same; the viewBox
    // simply grows by 2 × factor in each dimension.
    const extra = opts.extraPaddingFactor ?? 0
    if (extra > 0) {
      const padX2 = viewBox.width * extra
      const padY2 = viewBox.height * extra
      viewBox = {
        x: viewBox.x - padX2,
        y: viewBox.y - padY2,
        width: viewBox.width + 2 * padX2,
        height: viewBox.height + 2 * padY2,
      }
    }
  }

  // A tall portrait board used to paint inside a narrow SVG even though its
  // wrapper covered the full width. Zoomed content was clipped at that SVG's
  // sides. Address the horizontal gutters without changing px per map unit:
  // width grows in the same proportion as the viewBox, and height is fixed.
  if (!isLandscape && svgWidth < widthBudget) {
    const paddedWidth = width * widthBudget / svgWidth
    viewBox = { x: x - (paddedWidth - width) / 2, y, width: paddedWidth, height }
    svgWidth = widthBudget
    containerWidth = widthBudget
  }

  return {
    svgWidth,
    svgHeight,
    containerWidth,
    isLandscape,
    isDesktop,
    actionPanelWidth,
    viewBox,
    // Native map width — the element-sizing basis. NOT viewBox.width,
    // which is inflated by the landscape wrapper-fill padding above and
    // would make troop numbers grow with screen width. See the
    // MapDimensions.sizingWidth doc for the full rationale.
    sizingWidth: width,
  }
}

/**
 * Desktop landscape: a shorter canvas — half the map's spare vertical space
 * comes off the bottom, so the centred board rises halfway toward the top and
 * the controls bar follows the canvas bottom. Shared by the live map and the
 * snapshot so the board lands on the same pixels in both (no jump entering
 * or leaving a snapshot).
 */
export function desktopCanvasBottomSpace(
  dims: Pick<MapDimensions, 'svgWidth' | 'svgHeight' | 'isDesktop' | 'isLandscape'>,
  viewBox: { width: number; height: number } | undefined,
): number {
  if (!dims.isDesktop || !dims.isLandscape || !viewBox) return 0
  return Math.max(0, dims.svgHeight - dims.svgWidth * viewBox.height / viewBox.width) / 2
}
